// src/Lexprompt/pages/PromptListPage.jsx

import React, { useState, useContext, useEffect, useRef } from "react";
import { AuthContext } from "../../user/AuthContext";
import DepartmentFilter from "../components/DepartmentFilter";
import PromptCard from "./PromptCard";
import usePrompts from "../hooks/usePrompts";
import { builtinTemplates } from "./Templates";
import NewPromptPage from "./NewPromptPage"; // import the drag-and-drop modal

import "./PromptListPage.css";

export default function PromptListPage({ shareId = null }) {
  const currentUser = useContext(AuthContext);
  const [deptFilter, setDeptFilter] = useState("Trending");
  const actualFilter = deptFilter === "Trending" ? "All" : deptFilter;

  // Track whether “NewPromptPage” is visible
  const [showNewModal, setShowNewModal] = useState(false);

  // Load real prompts from Firestore (filtered by department)
  const { prompts, loading } = usePrompts(actualFilter);

  // Filter built-in templates by department
  const filteredTemplates =
    actualFilter === "All"
      ? builtinTemplates
      : builtinTemplates.filter((t) => t.department === actualFilter);

  // Combine templates + prompts into a single array for rendering
  const allCards = [...filteredTemplates, ...(!loading ? prompts : [])];

  // Refs map so we can scroll to and open popup on a specific card
  const refsMap = useRef({});

  // After prompts load, scroll to & open popup for shareId
  useEffect(() => {
    if (!loading && shareId) {
      const cardRef = refsMap.current[shareId];
      if (cardRef && cardRef.current) {
        cardRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        if (cardRef.current.openSharePopup) {
          cardRef.current.openSharePopup();
        }
      }
    }
  }, [loading, shareId]);

  return (
    <div className="prompt-list-page">
      {/* ─── Banner ─────────────────────────────────────────────── */}
      <div className="bannerContainer">
        <img src="/images/prompt.png" alt="Banner" className="banner" />
      </div>

      {/* ─── Top Bar: Department Filter + “Add My Prompt” / “Invite My Team” ─── */}
      <div className="prompt-list-header">
        <DepartmentFilter selected={deptFilter} onSelect={setDeptFilter} />

        <div className="prompt-list-actions">
          <button
            className="btn-primary small-button"
            onClick={() => setShowNewModal(true)}
          >
            + Add My Prompt
          </button>
          <button className="btn-secondary small-button">
            Invite My Team
          </button>
        </div>
      </div>

      {/* ─── Unified Card Grid: (templates first, then user prompts) ─────────── */}
      <div className="card-grid">
        {allCards.map((card) => {
          // Create a ref for each card if not already created
          if (!refsMap.current[card.id]) {
            refsMap.current[card.id] = React.createRef();
          }
          const isTemplate = builtinTemplates.some((t) => t.id === card.id);
          return (
            <PromptCard
              key={card.id}
              ref={refsMap.current[card.id]}
              prompt={card}
              isTemplate={isTemplate}
              highlight={shareId === card.id}
            />
          );
        })}
      </div>

      {/* ─── If no cards at all (templates + prompts) ─────────────────────── */}
      {!loading && allCards.length === 0 && (
        <p className="no-cards-text">No prompts found for “{actualFilter}.”</p>
      )}

      {/* ─── Render NewPromptPage modal when needed ─────────────────────────── */}
      {showNewModal && <NewPromptPage onClose={() => setShowNewModal(false)} />}
    </div>
  );
}
